/**
 * Template Migration Registry
 * 
 * Defines the current template version and all available migrations.
 * Following the sequential migration pattern for database schema evolution.
 */

// Current template schema version
export const CURRENT_TEMPLATE_VERSION = 6

/**
 * Template migration interface
 */
export interface TemplateMigration {
  fromVersion: number
  toVersion: number
  migrate: (template: unknown) => unknown
  description: string
}

/**
 * Complete list of all template migrations
 * 
 * Migrations are applied sequentially: V1→V2→V3→V4...
 * Each migration should be idempotent and handle edge cases gracefully.
 */
import { migrateV3ToV4 } from './v3ToV4'
import { migrateV4ToV5 } from './v4ToV5'
import { migrateV5ToV6 } from './v5ToV6'

export const TEMPLATE_MIGRATIONS: TemplateMigration[] = [
  {
    fromVersion: 3,
    toVersion: 4,
    migrate: migrateV3ToV4,
    description: 'Add legendCorner to each chart and bump version (V3→V4)'
  },
  {
    fromVersion: 4,
    toVersion: 5,
    migrate: migrateV4ToV5,
    description: 'Add legendMode to each chart (overlay/bottom/none) and bump version (V4→V5)'
  },
  {
    fromVersion: 5,
    toVersion: 6,
    migrate: migrateV5ToV6,
    description: 'Add optional Display Name to curve styling and bump version (V5→V6)'
  }
]

/**
 * Gets all migrations needed to upgrade from a source version to the current version
 * 
 * @param fromVersion - Starting version
 * @returns Array of migrations to apply in sequence
 */
export function getMigrationsPath(fromVersion: number): TemplateMigration[] {
  const path: TemplateMigration[] = []
  let currentVersion = fromVersion
  
  while (currentVersion < CURRENT_TEMPLATE_VERSION) {
    const nextMigration = TEMPLATE_MIGRATIONS.find(m => m.fromVersion === currentVersion)
    
    if (!nextMigration) {
      throw new Error(`No migration found from version ${currentVersion}`)
    }
    
    path.push(nextMigration)
    currentVersion = nextMigration.toVersion
  }
  
  return path
}

/**
 * Validates that all migrations form a complete chain to the current version
 * This should be called during app startup to ensure migration integrity
 */
export function validateMigrationChain(): void {
  // Check that we can reach current version from any supported version
  for (let version = 1; version < CURRENT_TEMPLATE_VERSION; version++) {
    try {
      getMigrationsPath(version)
      console.log(`✅ Migration path from V${version} to V${CURRENT_TEMPLATE_VERSION} is valid`)
    } catch (error) {
      console.warn(`⚠️  No migration path from V${version} to V${CURRENT_TEMPLATE_VERSION}:`, error)
    }
  }
}

// Future migration exports will go here 
